import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  MyLeadsTabBucketState,
  OpportunitiesState,
  OpportunitiesFiltersState,
} from "../../../Interfaces/ReduxSliceInterface";
import { Opportunity } from "../../../Interfaces/OpportunitiesInterface";
import {
  MY_LEADS_TAB_KEYS,
  type MyLeadsTabKey,
} from "../../../constants/myLeadsTabs";

/**
 * Entity key for grouping: patientId if present, else contactId, else _id.
 * Used to reorder opportunities so each entity's rows appear together (first-occurrence order).
 */
function getEntityKey(o: Opportunity): string {
  const pid = o.patientId?.trim();
  const cid = o.contactId?.trim();
  if (pid) return pid;
  if (cid) return cid;
  return o._id;
}

/**
 * Reorders opportunities so that entities (patient/contact) appear in first-occurrence order:
 * the entity that appears first in the list gets all its opportunities together first, then the next, etc.
 * Within each entity, API order is preserved. Current-page only (call with the page's array).
 */
function reorderOpportunitiesByEntityFirstOccurrence(
  opportunities: Opportunity[]
): Opportunity[] {
  if (opportunities.length === 0) return [];
  const orderOfEntities: string[] = [];
  const seen = new Set<string>();
  for (const o of opportunities) {
    const key = getEntityKey(o);
    if (!seen.has(key)) {
      seen.add(key);
      orderOfEntities.push(key);
    }
  }
  const byEntity = new Map<string, Opportunity[]>();
  for (const o of opportunities) {
    const key = getEntityKey(o);
    if (!byEntity.has(key)) byEntity.set(key, []);
    byEntity.get(key)!.push(o);
  }
  const result: Opportunity[] = [];
  for (const key of orderOfEntities) {
    const group = byEntity.get(key);
    if (group) result.push(...group);
  }
  return result;
}

export function createEmptyMyLeadsTabBucket(): MyLeadsTabBucketState {
  return {
    opportunities: [],
    loading: false,
    error: null,
    total: 0,
    totalPages: 0,
    hasMore: false,
    hasNext: false,
    hasPrevious: false,
    refetchRequestedAt: 0,
  };
}

function createInitialByTab(): OpportunitiesState["byTab"] {
  return MY_LEADS_TAB_KEYS.reduce(
    (acc, tab) => {
      acc[tab] = createEmptyMyLeadsTabBucket();
      return acc;
    },
    {} as OpportunitiesState["byTab"]
  );
}

const initialFilters: OpportunitiesFiltersState = {};

const initialState: OpportunitiesState = {
  activeTab: "all",
  page: 1,
  limit: 20,
  filters: initialFilters,
  todayContactedConnectivity: null,
  byTab: createInitialByTab(),
};

type TabPayload<T> = T & { tab: MyLeadsTabKey };

const opportunitiesSlice = createSlice({
  name: "opportunities",
  initialState,
  reducers: {
    setActiveMyLeadsTab: (state, action: PayloadAction<MyLeadsTabKey>) => {
      state.activeTab = action.payload;
    },
    setOpportunities: (
      state,
      action: PayloadAction<TabPayload<{ opportunities: Opportunity[] }>>
    ) => {
      state.byTab[action.payload.tab].opportunities =
        reorderOpportunitiesByEntityFirstOccurrence(
          action.payload.opportunities
        );
    },
    setOpportunitiesLoading: (
      state,
      action: PayloadAction<TabPayload<{ loading: boolean }>>
    ) => {
      state.byTab[action.payload.tab].loading = action.payload.loading;
    },
    setOpportunitiesError: (
      state,
      action: PayloadAction<TabPayload<{ error: string | null }>>
    ) => {
      state.byTab[action.payload.tab].error = action.payload.error;
    },
    setOpportunitiesMeta: (
      state,
      action: PayloadAction<
        TabPayload<{
          total: number;
          page: number;
          limit: number;
          totalPages: number;
          hasMore?: boolean;
          hasNext?: boolean;
          hasPrevious?: boolean;
        }>
      >
    ) => {
      const bucket = state.byTab[action.payload.tab];
      bucket.total = action.payload.total;
      bucket.totalPages = action.payload.totalPages;
      bucket.hasMore =
        action.payload.hasMore ?? action.payload.hasNext ?? false;
      bucket.hasNext =
        action.payload.hasNext ?? action.payload.hasMore ?? false;
      bucket.hasPrevious =
        action.payload.hasPrevious ?? action.payload.page > 1;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    requestOpportunitiesRefetch: (
      state,
      action: PayloadAction<MyLeadsTabKey | undefined>
    ) => {
      const now = Date.now();
      const tabs = action.payload ? [action.payload] : MY_LEADS_TAB_KEYS;
      for (const tab of tabs) {
        state.byTab[tab].refetchRequestedAt = now;
      }
    },
    setOpportunitiesFilters: (
      state,
      action: PayloadAction<OpportunitiesFiltersState>
    ) => {
      state.filters = action.payload;
    },
    clearOpportunitiesFilters: (state) => {
      state.filters = initialFilters;
    },
    setTodayContactedConnectivity: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.todayContactedConnectivity = action.payload;
    },
    clearOpportunities: (state) => {
      state.activeTab = "all";
      state.page = 1;
      state.limit = 50;
      state.filters = initialFilters;
      state.todayContactedConnectivity = null;
      state.byTab = createInitialByTab();
    },
  },
});

export const {
  setActiveMyLeadsTab,
  setOpportunities,
  setOpportunitiesLoading,
  setOpportunitiesError,
  setOpportunitiesMeta,
  setPage,
  requestOpportunitiesRefetch,
  setOpportunitiesFilters,
  clearOpportunitiesFilters,
  setTodayContactedConnectivity,
  clearOpportunities,
} = opportunitiesSlice.actions;

export default opportunitiesSlice.reducer;
